/* eslint-disable react/prop-types */
/**
 * WhatsNewModal — the release-notes surface: "here is what changed in the
 * build you are now looking at".
 *
 * TWO WAYS IN, ONE COMPONENT.
 *   1. Unprompted, once per new build. Mounted at the nav bar, it opens
 *      itself when this browser's last-acknowledged version is older than
 *      the running build and there are notes in between. Closing it records
 *      the current version, so it does not ask twice.
 *   2. On demand, from the user menu ("What's new"), which passes `open` and
 *      shows the whole history rather than only the unseen part.
 *
 * WHY A MODAL HERE, WHEN AnnouncementBanner ARGUES FOR BANNERS. The two
 * surfaces answer different questions. An outage notice has to land while
 * someone is mid-task at 4:55pm, which a banner does and a modal does not.
 * Release notes are the opposite: they are only interesting at the moment the
 * build changes under you, they are read once, and they explain why the
 * screen looks different from yesterday. That is exactly the on-open moment a
 * modal owns — and unlike the outage modal, this one fires only when the
 * version number actually moved, which for this app is a handful of times a
 * month rather than every session.
 *
 * The content is bundled, not fetched (see utils/whatsNew.js), so this
 * renders instantly and correctly offline, and can never describe a build
 * other than the one it shipped in.
 *
 * Props:
 *   open    : boolean  — force the full history open (nav-triggered)
 *   onClose : () => void
 */
import { useEffect, useMemo, useState } from "react";
import { Button, Empty, Modal, Tag, Typography } from "antd";
import { RocketOutlined } from "@ant-design/icons";

import notes from "../data/releaseNotes.json";
import {
  readSeenVersion,
  releasesSince,
  sortReleases,
  writeSeenVersion,
} from "../utils/whatsNew";

const { Text, Paragraph } = Typography;

// The running build, injected by Vite's `define` (see vite.config.js). The
// guard keeps this component importable where the define is absent (a bare
// unit test, Storybook, a node script).
const appVersion = () => {
  try {
    // eslint-disable-next-line no-undef
    return typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : null;
  } catch {
    return null;
  }
};

/** A date the way a landman writes it, not an ISO timestamp. */
const formatDate = (value) => {
  if (!value) return "";
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const ReleaseBlock = ({ release, latest }) => (
  <div style={{ marginBottom: 20 }}>
    <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
      <Text strong>{formatDate(release.date)}</Text>
      <Text type="secondary" style={{ fontSize: 12 }}>
        v{release.version}
      </Text>
      {latest && <Tag color="blue">Latest</Tag>}
    </div>
    <ul style={{ marginTop: 6, marginBottom: 0, paddingLeft: 20 }}>
      {release.highlights.map((line, i) => (
        <li key={i} style={{ marginBottom: 4 }}>
          {line}
        </li>
      ))}
    </ul>
  </div>
);

const WhatsNewModal = ({ open = false, onClose }) => {
  const current = appVersion();

  // The unseen set is computed once, on mount. Deliberately not reactive:
  // the bundle cannot change under a mounted page, so re-deriving it would
  // only risk re-opening the modal after someone dismissed it.
  const [unseen, setUnseen] = useState([]);
  const [autoOpen, setAutoOpen] = useState(false);

  useEffect(() => {
    const seen = readSeenVersion();
    if (!seen) {
      // First visit on this browser — record where we are and stay quiet.
      writeSeenVersion(current);
      return;
    }
    const rows = releasesSince(notes.releases, seen, current);
    setUnseen(rows);
    setAutoOpen(rows.length > 0);
  }, [current]);

  const all = useMemo(() => sortReleases(notes.releases), []);

  const showingAll = open;
  const rows = showingAll ? all : unseen;
  const visible = showingAll || autoOpen;

  const close = () => {
    // Either way in, closing means "read": the unseen notes were on screen.
    writeSeenVersion(current);
    setAutoOpen(false);
    setUnseen([]);
    onClose?.();
  };

  return (
    <Modal
      open={visible}
      onCancel={close}
      title={
        <span>
          <RocketOutlined style={{ marginRight: 8 }} />
          {showingAll ? "What's new" : "What's new in Deal Capture"}
        </span>
      }
      footer={
        <Button type="primary" onClick={close}>
          {showingAll ? "Close" : "Got it"}
        </Button>
      }
      width={620}
    >
      {rows.length === 0 ? (
        <Empty description="No release notes yet." />
      ) : (
        <>
          {!showingAll && (
            <Paragraph type="secondary">
              The app updated since you were last here. Here is what changed.
            </Paragraph>
          )}
          <div style={{ maxHeight: "55vh", overflowY: "auto" }}>
            {rows.map((r, i) => (
              <ReleaseBlock key={r.version} release={r} latest={i === 0} />
            ))}
          </div>
        </>
      )}
    </Modal>
  );
};

export default WhatsNewModal;
